import React, { useCallback, useEffect, useState } from 'react'

import { Box } from '@mui/material'

import { ProductModel } from '@repo/ui'

/**
 * VRG-780 — the product page's lead slide: the tag, turning, until you take hold of it.
 *
 * ## It is the INVERSE of the grid tile, deliberately
 *
 * In the catalogue (`ProductModelHover`) a still is the resting state and hover reveals
 * the model, because eight live canvases turning at once is noise nobody browsing a
 * grid asked for. Here there is ONE product and the model IS the subject, so it turns
 * on arrival and keeps turning.
 *
 * Nothing mounts or unmounts either way: the viewer stays up and only `auto-rotate`
 * moves, which is the same gating the grid uses.
 *
 * ## ⚠️ HOVER DOES NOT STOP IT. PRESSING DOES.
 *
 * This first gated the spin on `mouseenter`, on the reasoning that a pointer on a model
 * is someone about to grab it. That was wrong in use (Spencer, 2026-10-31): the model
 * fills the gallery frame, so a pointer crossing the page to reach the size selector or
 * the thumbnails passes straight over it, and the subject of the page stopped dead
 * every time the cursor went anywhere near. Hover is not an intention, it is traffic.
 *
 * A press is an intention. `pointerdown` parks the spin for exactly as long as the
 * button is held, which is the window in which someone is actually turning the part,
 * and release hands it back. One consequence worth naming: a press with no drag — a
 * plain click on the model — stops it for those few hundred milliseconds and then lets
 * it go again. That reads as the part answering the click rather than ignoring it, so
 * it is left as is.
 *
 * `pointerup` is listened for on the WINDOW rather than on this box, because a drag
 * that turns the tag usually ends with the pointer outside the frame it started in. On
 * the element alone the release would be missed and the spin would never come back.
 *
 * ## `swiper-no-swiping` is load-bearing, not defensive
 *
 * This renders INSIDE a Swiper slide, and Swiper claims horizontal drags anywhere in
 * its container. Without the class, dragging left to turn the tag advances the gallery
 * to the next picture instead — the model's one interaction, taken by its frame. The
 * class is Swiper's own opt-out (`noSwipingClass`, default `swiper-no-swiping`) and is
 * scoped to this slide, so the arrows and the other slides still swipe normally.
 *
 * It is the same class of defect `ProductModelHover` records for the grid, where a drag
 * ended in a click and opened the product page. Both come from putting a draggable
 * object inside something that is itself draggable; both are invisible until someone
 * actually drags.
 */
export interface TagModelSlideProps {
  /** The GLB. Only ever passed when `tagModelAsset` confirmed one exists. */
  src: string
  /** Product title — the viewer's alt text. */
  title?: string
}

export const TagModelSlide = ({ src, title }: TagModelSlideProps) => {
  /* True only while a pointer button is down on the model. See the docblock. */
  const [held, setHeld] = useState(false)

  const take = useCallback(() => setHeld(true), [])

  useEffect(() => {
    if (!held) return
    const release = () => setHeld(false)
    /* `pointercancel` as well as `pointerup`: a touch that turns into a browser
       gesture — a scroll the page claims, a long-press menu — never fires `up`,
       and without this the spin would stay parked for the life of the page. */
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
    }
  }, [held])

  return (
    <Box
      className="swiper-no-swiping"
      onPointerDown={take}
      sx={{ width: '100%', height: '100%' }}
    >
      <ProductModel
        src={src}
        title={title}
        aspectRatio="1 / 1"
        /* The Slideshow paints the media plate on `.swiper`, so the viewer sits on
           it rather than painting a second surface over it — a photograph and a
           model in the same gallery land on one colour (theme/mediaPlate.ts). */
        surface="bare"
        autoRotate={!held}
        /*
         * Reset and AR, in the frame's bottom-right.
         *
         * This is the surface where both earn their place: the model is the page's
         * subject, a buyer who has dragged it to an odd angle has no other way back,
         * and on a phone AR is the thing a physical part most wants.
         *
         * NOT enabled on the grid tile, deliberately. There the model only exists
         * while the pointer is on it, so mouse-out already resets it and a button
         * would be a second answer to a question the hover has answered; and the
         * tile has no touch path at all (`ProductModelHover`'s docblock), so an AR
         * button on it could never be reached on the only device that can use it.
         */
        controls
        /* 0, not the component's 1000: the slide arrives already turning, and a
           second of stillness first reads as a model that failed to load. */
        autoRotateDelayMs={0}
        /* The hint line would add height below a square slide and break the
           gallery's 1:1 frame. The drag instruction needs its own home on this
           page — see the task file. */
        showHint={false}
      />
    </Box>
  )
}
